import { composePaths } from '@jsonforms/core';

const rootPaths = new Set(['', 'root']);

/**
 * Build the full dot-notation path from the entity-data root down to a
 * property's value, accounting for the entity's backend wrapping
 * convention:
 *
 *  - config-app-backend wraps data under `attributes.domainEntityAttributes.…`
 *    where the inner `domainEntityAttributes` segment in `formPath` is the
 *    schema-side `domainEntityAttributes` property; the outer one is the
 *    API envelope. Both are present in stored data — see the
 *    masonry-questionnaire fixtures.
 *  - legacy backends use a single `attributes.…` wrapper.
 *
 * `formPath` is the JSONForms-side path (rooted at the form's data root —
 * typically the entity-tree node path). `propertyPath` is the relative
 * path the calling control reports via `props.path`. Either may be empty
 * to indicate "no further nesting beyond the base".
 *
 * Useful when a control needs to address its own data location absolutely,
 * for example to walk back up the tree for scoped data sources via
 * `findEnclosingItemPath`.
 */
export const getFullEntityDataPath = (
  formPath: string | undefined,
  propertyPath: string,
  isUsingConfigAppBackend: boolean,
): string => {
  const baseDataPath = isUsingConfigAppBackend
    ? 'attributes.domainEntityAttributes'
    : 'attributes';
  const formSegment = formPath && !rootPaths.has(formPath) ? formPath : '';
  const tail = formSegment
    ? composePaths(formSegment, propertyPath)
    : propertyPath;
  return tail ? composePaths(baseDataPath, tail) : baseDataPath;
};
